import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Upload, Loader2, Trash2, Search, X, Image as ImageIcon, Video, Play, Sparkles } from "lucide-react";
import { getApiUrl } from "@/utils/api";
import { resolveImageUrl } from "@/utils/imageUrl";
import axios from "axios";
import ImageEditor from "@/components/ImageEditor";

const API = getApiUrl();

/**
 * Image Library Modal - Browse, upload, edit, and select images or videos
 * Props:
 *   open: boolean
 *   onClose: function
 *   onSelect: function(url, { isVideo }) - called when an item is selected
 *   allowEdit: boolean - whether to show edit button
 */
export function ImageLibraryModal({ open, onClose, onSelect, allowEdit = true }) {
  const [activeTab, setActiveTab] = useState("images");
  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [icons, setIcons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [editingImage, setEditingImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedIcon, setSelectedIcon] = useState(null);

  // Load images from server
  const loadImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/image-library`, {
        withCredentials: true
      });
      setImages(response.data.images || []);
    } catch (error) {
      console.error("Failed to load images:", error);
      toast.error("Failed to load image library");
    } finally {
      setLoading(false);
    }
  };

  const loadVideos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/video-library`, {
        withCredentials: true
      });
      setVideos(response.data.videos || []);
    } catch (error) {
      console.error("Failed to load videos:", error);
      toast.error("Failed to load video library");
    } finally {
      setLoading(false);
    }
  };

  const loadIcons = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/icons`, { withCredentials: true });
      setIcons(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Failed to load icons:", error);
      toast.error("Failed to load icon gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadImages();
      loadVideos();
      loadIcons();
      setActiveTab("images");
      setSearchTerm("");
      setSelectedImage(null);
      setSelectedVideo(null);
      setSelectedIcon(null);
    }
  }, [open]);

  // Handle bulk file upload (images or videos, decided by accepted mime type)
  const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append("files", file);
      });
      
      const response = await axios.post(
        `${API}/admin/upload-multiple`,
        formData,
        {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" }
        }
      );

      if (response.data.uploaded && response.data.uploaded.length > 0) {
        toast.success(`${response.data.uploaded.length} file(s) uploaded`);
        loadImages();
        loadVideos();
      }
      
      if (response.data.errors && response.data.errors.length > 0) {
        response.data.errors.forEach((er) => {
          toast.error(`${er.file}: ${er.reason}`);
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload files");
    } finally {
      setUploading(false);
    }
  };

  // Handle image/video deletion
  const handleDelete = async (filename) => {
    if (!confirm("Are you sure you want to delete this file?")) return;
    
    try {
      await axios.delete(`${API}/admin/image-library/${filename}`, {
        withCredentials: true
      });
      toast.success("File deleted");
      loadImages();
      loadVideos();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete file");
    }
  };

  // Filter images/videos based on search
  const filteredImages = images.filter(img => 
    !searchTerm || 
    img.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    img.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredVideos = videos.filter(vid =>
    !searchTerm ||
    vid.original_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vid.filename?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredIcons = icons.filter(icon =>
    !searchTerm || icon.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle selection (Insert button)
  const handleSelect = () => {
    if (activeTab === "videos" && selectedVideo) {
      onSelect(selectedVideo.url, { isVideo: true });
      onClose();
    } else if (activeTab === "images" && selectedImage) {
      onSelect(selectedImage.url, { isVideo: false });
      onClose();
    } else if (activeTab === "icons" && selectedIcon) {
      onSelect(selectedIcon.image_url, { isVideo: false });
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col rounded-none">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Media Library</DialogTitle>
          </DialogHeader>

          {/* Tab switcher */}
          <div className="flex gap-1 border-b" data-testid="media-library-tabs">
            <button
              type="button"
              onClick={() => setActiveTab("images")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "images" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="media-library-tab-images"
            >
              <ImageIcon className="h-4 w-4" /> Images ({images.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("videos")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "videos" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="media-library-tab-videos"
            >
              <Video className="h-4 w-4" /> Videos ({videos.length})
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("icons")}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === "icons" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid="media-library-tab-icons"
            >
              <Sparkles className="h-4 w-4" /> Icons ({icons.length})
            </button>
          </div>

          {/* Upload Area */}
          {activeTab === "icons" ? (
            <div className="border-2 border-dashed rounded-none p-4 text-center bg-muted/20 text-sm text-muted-foreground">
              Create new icons in System &gt; Tools &gt; Icon Generator, then save them to the gallery to use them here.
            </div>
          ) : (
          <div className="border-2 border-dashed rounded-none p-4 text-center bg-muted/20">
            <input
              type="file"
              id="library-upload"
              accept={activeTab === "videos" ? "video/mp4,video/webm,video/quicktime" : "image/jpeg,image/png,image/webp,image/gif"}
              multiple
              onChange={(e) => handleFileUpload(e.target.files)}
              className="hidden"
              data-testid="media-library-upload-input"
            />
            <label htmlFor="library-upload" className="cursor-pointer">
              {uploading ? (
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  <span className="text-sm">Uploading...</span>
                </div>
              ) : activeTab === "videos" ? (
                <div className="flex items-center justify-center gap-2">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Upload Videos</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    (MP4, WebM, MOV • Max 50MB)
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <Upload className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium">Upload Images</span>
                  <span className="text-xs text-muted-foreground ml-2">
                    (JPEG, PNG, WebP, GIF • Max 20MB)
                  </span>
                </div>
              )}
            </label>
          </div>
          )}

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={activeTab === "videos" ? "Search videos by filename..." : activeTab === "icons" ? "Search icons by name..." : "Search images by filename..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-10 rounded-none"
              data-testid="media-library-search-input"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2"
              >
                <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Grid */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : activeTab === "videos" ? (
              filteredVideos.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <p className="text-sm">
                    {searchTerm ? "No videos found matching your search" : "No videos in library yet"}
                  </p>
                  <p className="text-xs mt-1">Upload videos to get started</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3 p-1" data-testid="media-library-video-grid">
                  {filteredVideos.map((vid, idx) => (
                    <div
                      key={`${vid.filename}-${idx}`}
                      className={`relative group aspect-square bg-black border-2 cursor-pointer transition-all ${
                        selectedVideo?.filename === vid.filename
                          ? "border-primary ring-2 ring-primary"
                          : "border-transparent hover:border-primary/50"
                      }`}
                      onClick={() => setSelectedVideo(vid)}
                      data-testid={`media-library-video-item-${vid.filename}`}
                    >
                      <video
                        src={resolveImageUrl(vid.url)}
                        className="w-full h-full object-cover"
                        muted
                        preload="metadata"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-colors">
                        <Play className="h-8 w-8 text-white/90 fill-white/90" />
                      </div>

                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center gap-2 p-2">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(vid.filename);
                          }}
                          className="rounded-none text-xs"
                          data-testid={`media-library-delete-video-${vid.filename}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>

                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {vid.original_name || vid.filename}
                      </div>

                      {selectedVideo?.filename === vid.filename && (
                        <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : activeTab === "icons" ? (
              filteredIcons.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <p className="text-sm">
                    {searchTerm ? "No icons found matching your search" : "No icons in the gallery yet"}
                  </p>
                  <p className="text-xs mt-1">Create one in the Icon Generator to get started</p>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-3 p-1" data-testid="media-library-icon-grid">
                  {filteredIcons.map((icon) => (
                    <div
                      key={icon.id}
                      className={`relative group aspect-square bg-muted border-2 cursor-pointer transition-all flex flex-col items-center justify-center p-2 ${
                        selectedIcon?.id === icon.id
                          ? "border-primary ring-2 ring-primary"
                          : "border-transparent hover:border-primary/50"
                      }`}
                      onClick={() => setSelectedIcon(icon)}
                      data-testid={`media-library-icon-item-${icon.id}`}
                    >
                      <img
                        src={resolveImageUrl(icon.image_url)}
                        alt={icon.name}
                        className="w-full h-full object-contain"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {icon.name}
                      </div>
                      {selectedIcon?.id === icon.id && (
                        <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                          <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )
            ) : filteredImages.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                <p className="text-sm">
                  {searchTerm ? "No images found matching your search" : "No images in library yet"}
                </p>
                <p className="text-xs mt-1">Upload images to get started</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-3 p-1">
                {filteredImages.map((img, idx) => (
                  <div
                    key={`${img.filename}-${idx}`}
                    className={`relative group aspect-square bg-muted border-2 cursor-pointer transition-all ${
                      selectedImage?.filename === img.filename
                        ? "border-primary ring-2 ring-primary"
                        : "border-transparent hover:border-primary/50"
                    }`}
                    onClick={() => setSelectedImage(img)}
                  >
                    <img
                      src={resolveImageUrl(img.url)}
                      alt={img.original_name || img.filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/150?text=Error";
                      }}
                    />
                    
                    {/* Actions overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      {allowEdit && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingImage(img);
                          }}
                          className="rounded-none text-xs"
                        >
                          Edit
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(img.filename);
                        }}
                        className="rounded-none text-xs"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Filename tooltip */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-[10px] p-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                      {img.original_name || img.filename}
                    </div>

                    {/* Selected checkmark */}
                    {selectedImage?.filename === img.filename && (
                      <div className="absolute top-2 right-2 bg-primary text-white rounded-full p-1">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <p className="text-xs text-muted-foreground">
              {activeTab === "videos"
                ? `${filteredVideos.length} video(s) ${searchTerm ? "found" : ""}`
                : activeTab === "icons"
                ? `${filteredIcons.length} icon(s) ${searchTerm ? "found" : ""}`
                : `${filteredImages.length} image(s) ${searchTerm ? "found" : ""}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={onClose}
                className="rounded-none"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSelect}
                disabled={activeTab === "videos" ? !selectedVideo : activeTab === "icons" ? !selectedIcon : !selectedImage}
                className="rounded-none"
                data-testid="media-library-insert-btn"
              >
                {activeTab === "videos" ? "Insert Video" : activeTab === "icons" ? "Insert Icon" : "Insert Image"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Editor Modal */}
      <ImageEditor
        open={editingImage !== null}
        onClose={() => setEditingImage(null)}
        sourceUrl={editingImage?.url}
        skipWatermark={true}
        onSaved={(newUrl) => {
          toast.success("Image updated");
          setEditingImage(null);
          loadImages();
        }}
      />
    </>
  );
}

export default ImageLibraryModal;
